// src/hooks/useMediaManager.ts
"use client";

import { useEffect, useRef } from 'react';

type MediaElement = HTMLAudioElement | HTMLVideoElement;

class MediaManager {
    private currentMedia: MediaElement | null = null;
    private listeners: Set<(media: MediaElement | null) => void> = new Set();

    register(media: MediaElement) {
        // Pause any currently playing media
        if (this.currentMedia && this.currentMedia !== media) {
            this.currentMedia.pause();
        }

        // Set as current media
        this.currentMedia = media;
        this.notifyListeners();

        // Handle when media ends
        const onEnded = () => {
            if (this.currentMedia === media) {
                this.currentMedia = null;
                this.notifyListeners();
            }
        };

        const onPlay = () => {
            if (this.currentMedia !== media) {
                media.pause();
            } else {
                this.notifyListeners();
            }
        };

        media.addEventListener('ended', onEnded);
        media.addEventListener('play', onPlay);

        // Store cleanup function
        return () => {
            media.removeEventListener('ended', onEnded);
            media.removeEventListener('play', onPlay);
            if (this.currentMedia === media) {
                this.currentMedia = null;
                this.notifyListeners();
            }
        };
    }

    pauseCurrent() {
        this.currentMedia?.pause();
    }

    subscribe(callback: (media: MediaElement | null) => void) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    private notifyListeners() {
        this.listeners.forEach(listener => listener(this.currentMedia));
    }
}

// Singleton instance
const mediaManager = new MediaManager();

/** Pause any inline <video>/<audio> (used when the unified player starts). */
export function pauseCurrentMedia() {
    mediaManager.pauseCurrent();
}

export function useMediaManager() {
    const cleanupRef = useRef<(() => void) | null>(null);

    const registerMedia = (media: MediaElement | null) => {
        // Clean up previous registration
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }

        if (media) {
            cleanupRef.current = mediaManager.register(media);
        }
    };

    return { registerMedia };
}